/** Capture z e-maila (Faza 7): stan pollingu IMAP + ręczny strzał do testu. */

import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../api";
import type { EmailPollResult } from "./types";
import { settingsKeys } from "./SettingsPage";

function formatMoment(value: string | null | undefined): string {
  if (!value) return "nigdy";
  const at = new Date(value);
  return Number.isNaN(at.getTime()) ? String(value) : at.toLocaleString("pl-PL");
}

function summary(result: EmailPollResult): string {
  if (result.skipped_reason) return result.skipped_reason;
  if (result.error) return `Błąd: ${result.error}`;
  return `Pobrano ${result.fetched}, wrzucono ${result.captured}, duplikatów ${result.duplicates}.`;
}

export function EmailSection() {
  const qc = useQueryClient();
  const { data, isLoading, error } = useQuery({
    queryKey: settingsKeys.email,
    queryFn: api.emailStatus,
  });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<string | null>(null);

  async function poll() {
    setBusy(true);
    setResult(null);
    try {
      setResult(summary(await api.emailPoll()));
      void qc.invalidateQueries({ queryKey: settingsKeys.email });
    } catch (exc) {
      setResult(exc instanceof Error ? exc.message : String(exc));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="card settings__section">
      <h2 className="card__title">Capture z e-maila</h2>
      <p className="muted">
        Nieprzeczytane wiadomości ze skrzynki lądują w inboxie i dostają flagę „przeczytane”.
        Konfiguracja jest w <code>.env</code> (IMAP_HOST, IMAP_USER, IMAP_PASSWORD…) — tu widać
        tylko, czy to działa.
      </p>

      {isLoading && <p className="muted">Ładowanie…</p>}
      {error && <p className="error">Nie udało się pobrać stanu skrzynki.</p>}

      {data && (
        <>
          {!data.configured && (
            <p className="error">
              Brak konfiguracji IMAP — ustaw <code>IMAP_HOST</code>, <code>IMAP_USER</code> i
              <code> IMAP_PASSWORD</code> w <code>.env</code>.
            </p>
          )}
          {data.configured && !data.enabled && (
            <p className="muted">
              Skrzynka skonfigurowana, ale polling wyłączony (<code>EMAIL_CAPTURE_ENABLED=0</code>).
              Przyciskiem poniżej można sprawdzić ją ręcznie.
            </p>
          )}

          <ul className="rows">
            <li className="row">
              <div className="row__main">
                <span className="row__title">Skrzynka</span>
                <span className="row__meta">
                  {data.configured ? `${data.user} @ ${data.host} / ${data.folder}` : "—"}
                </span>
              </div>
            </li>
            <li className="row">
              <div className="row__main">
                <span className="row__title">Polling</span>
                <span className="row__meta">
                  {data.enabled ? `co ${data.poll_minutes} min` : "wyłączony"}
                </span>
              </div>
            </li>
            <li className="row">
              <div className="row__main">
                <span className="row__title">Ostatni poll</span>
                <span className="row__meta">
                  {formatMoment(data.last_poll)}
                  {data.last_captured != null && ` · wrzutek: ${data.last_captured}`}
                </span>
              </div>
            </li>
            <li className="row">
              <div className="row__main">
                <span className="row__title">Ostatni udany</span>
                <span className="row__meta">
                  {formatMoment(data.last_success)} · łącznie wrzutek: {data.total_captured}
                </span>
              </div>
            </li>
          </ul>

          {data.last_error && <p className="error">Ostatni błąd: {data.last_error}</p>}
        </>
      )}

      <div className="clarify__buttons">
        <button
          className="btn btn--primary"
          disabled={busy || !data?.configured}
          onClick={() => void poll()}
        >
          {busy ? "Sprawdzam…" : "Sprawdź skrzynkę teraz"}
        </button>
      </div>
      {result && <p className="muted">{result}</p>}
    </section>
  );
}
